import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Brain,
    Send,
    Loader2,
    TrendingUp,
    Lightbulb,
    FileText,
    BarChart3,
    AlertCircle,
    CheckCircle,
    ChevronRight,
    RefreshCw,
    Sparkles,
    Target,
    List,
    Download
} from 'lucide-react';
import duckdbService from '../services/duckdbService';
import exportService from '../services/exportService';
import usageService from '../services/usageService';

// Helper function to safely render values that might be objects
const safeRender = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return value.map(v => safeRender(v)).join(', ');
    if (typeof value === 'object') {
        // For objects with description or similar fields, just return the description
        if (value.description) return value.description;
        if (value.text) return value.text;
        if (value.value) return safeRender(value.value);
        // Otherwise format as key-value pairs on separate lines
        return Object.entries(value)
            .map(([k, v]) => `${k.replace(/_/g, ' ').toUpperCase()}: ${safeRender(v)}`)
            .join('\n');
    }
    return String(value);
};

// Helper to safely extract array of strings from potentially nested data
const safeArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) {
        return data.map(item => safeRender(item));
    }
    if (typeof data === 'object') {
        return Object.entries(data).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${safeRender(v)}`);
    }
    return [safeRender(data)];
};

const BusinessAnalysis = ({ selectedFile, onFileSelect, mode, isAuthenticated, onRequireAuth, onUsageUpdate }) => {
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const [executiveSummaryLoading, setExecutiveSummaryLoading] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [executiveSummary, setExecutiveSummary] = useState(null);
    const [error, setError] = useState(null);
    const [analysisHistory, setAnalysisHistory] = useState([]);
    const [businessContext, setBusinessContext] = useState(null);

    const getUploadDateLabel = (file) => {
        if (!file) return 'Unknown';
        if (file.created_at) {
            const date = new Date(file.created_at);
            if (!Number.isNaN(date.getTime())) {
                return date.toLocaleDateString();
            }
        }
        if (file.upload_time) {
            const ms = file.upload_time < 1e12 ? file.upload_time * 1000 : file.upload_time;
            const date = new Date(ms);
            if (!Number.isNaN(date.getTime())) {
                return date.toLocaleDateString();
            }
        }
        return 'Unknown';
    };

    const loadExecutiveSummary = useCallback(async () => {
        if (!selectedFile) return;

        setExecutiveSummaryLoading(true);
        try {
            const columns = selectedFile.columns || [];
            const firstColumn = columns[0] || 'column';
            const rowCount = Number(selectedFile.row_count || 0);

            // Generate a simple executive summary using available data
            const summary = {
                key_metrics: {
                    total_rows: rowCount,
                    total_columns: columns.length,
                    file_name: selectedFile.filename,
                    upload_date: getUploadDateLabel(selectedFile)
                },
                recommended_analyses: [
                    `Show me the first 10 rows`,
                    `What are the unique values in ${firstColumn}?`,
                    `Count records by ${firstColumn}`,
                    `Show me statistics for all columns`
                ]
            };

            const businessContext = {
                description: `Dataset contains ${rowCount.toLocaleString()} records across ${columns.length} columns. Available columns: ${columns.slice(0, 5).join(', ')}${columns.length > 5 ? '...' : ''}`,
                industry: 'Data Analysis'
            };

            setExecutiveSummary(summary);
            setBusinessContext(businessContext);
        } catch (err) {
            console.error('Executive summary failed:', err);
        } finally {
            setExecutiveSummaryLoading(false);
        }
    }, [selectedFile]);

    // Load executive summary when file changes
    useEffect(() => {
        if (selectedFile) {
            loadExecutiveSummary();
        } else {
            setExecutiveSummary(null);
            setBusinessContext(null);
        }
    }, [selectedFile, loadExecutiveSummary]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedFile) {
            setError('Please select a file first');
            return;
        }

        if (!question.trim()) {
            setError('Please enter a business question');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            if (mode !== 'demo') {
                if (!isAuthenticated) {
                    setError('Create a free account to upload and query your own files.');
                    if (onRequireAuth) onRequireAuth();
                    setLoading(false);
                    return;
                }
                await usageService.recordQuery();
                if (onUsageUpdate) onUsageUpdate();
            }

            const datasetInfo = duckdbService.getDatasetInfo(selectedFile.file_id);
            if (!datasetInfo) {
                throw new Error('Dataset not found. Please upload the file again.');
            }

            // Import nlpService
            const nlpService = (await import('../services/nlpService')).default;

            // Generate SQL using NLP
            const sql = await nlpService.translateToSQL(
                question.trim(),
                datasetInfo.columns,
                datasetInfo.tableName
            );

            console.log('Generated SQL:', sql);

            // Execute query
            const queryResult = await duckdbService.executeQuery(sql, selectedFile.file_id);

            console.log('Query results:', queryResult);

            // Generate natural answer with insights
            const answer = await nlpService.generateNaturalAnswer(
                question.trim(),
                sql,
                queryResult.result,
                queryResult.row_count
            );

            console.log('Generated answer:', answer);

            // Create analysis result with insights
            const result = {
                success: true,
                sql,
                results: queryResult.result || [],
                rowCount: queryResult.row_count || 0,
                executionTime: queryResult.execution_time_ms,
                insights: {
                    key_insight: answer,
                    detailed_analysis: `Based on your query, I found ${queryResult.row_count} matching records. The data shows ${queryResult.result?.length > 0 ? 'results across ' + Object.keys(queryResult.result[0]).length + ' columns' : 'no results'}.`,
                    recommendations: [
                        'Review the data patterns in the results',
                        'Consider filtering by additional criteria',
                        'Export results for further analysis'
                    ],
                    follow_up_questions: [
                        'Show me more details about these results',
                        'What are the trends over time?',
                        'How does this compare to other segments?'
                    ]
                }
            };

            console.log('Setting analysis:', result);
            setAnalysis(result);
            setAnalysisHistory(prev => [{
                question: question.trim(),
                timestamp: new Date().toISOString(),
                ...result
            }, ...prev.slice(0, 9)]);
            setQuestion('');
        } catch (err) {
            console.error('Analysis error:', err);
            const detail = err?.response?.data?.detail;
            const message = detail || (err?.message === 'auth_required'
                ? 'Create a free account to upload and query your own files.'
                : err?.message);
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleSuggestedQuestion = (q) => {
        setQuestion(q);
    };

    if (!selectedFile) {
        return (
            <div className="flex items-center justify-center h-96">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center"
                >
                    <div className="w-16 h-16 bg-sand-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-[rgba(32,30,29,0.12)]">
                        <Brain className="w-8 h-8 text-sand-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-ink mb-2">No Data Selected</h3>
                    <p className="text-sand-700">Please upload or select a file to start AI-powered analysis</p>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            {/* Executive Summary Card */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card"
            >
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-sand-100 rounded-lg border border-[rgba(32,30,29,0.12)]">
                            <Brain className="w-5 h-5 text-ink" />
                        </div>
                        <h2 className="text-xl font-semibold text-ink">AI Business Intelligence</h2>
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={loadExecutiveSummary}
                        disabled={executiveSummaryLoading}
                        className="p-2 text-bill-600 hover:bg-bill-100 rounded-lg transition-colors"
                    >
                        <RefreshCw className={`w-5 h-5 ${executiveSummaryLoading ? 'animate-spin' : ''}`} />
                    </motion.button>
                </div>

                {executiveSummaryLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 text-bill-500 animate-spin" />
                        <span className="ml-3 text-sand-600">AI is analyzing your dataset...</span>
                    </div>
                ) : executiveSummary ? (
                    <div className="space-y-6">
                        {/* Business Context */}
                        {businessContext && (
                            <div className="p-4 bg-sand-100 rounded-xl border border-[rgba(32,30,29,0.12)]">
                                <div className="flex items-center gap-2 text-base font-semibold text-ink mb-3">
                                    <Target className="w-5 h-5" />
                                    Business Context
                                </div>
                                {typeof businessContext === 'object' && businessContext.description ? (
                                    <div className="space-y-3">
                                        <p className="text-sand-700 text-base leading-relaxed">{businessContext.description}</p>
                                        {businessContext.industry && (
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-sand-600">Industry:</span>
                                                <span className="text-sand-700 font-medium">{businessContext.industry}</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-sand-700 text-base leading-relaxed whitespace-pre-line">{safeRender(businessContext)}</p>
                                )}
                            </div>
                        )}

                        {/* Key Metrics */}
                        {executiveSummary.key_metrics && (
                            <div>
                                <h4 className="text-base font-semibold text-ink mb-3 flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-ink" />
                                    Key Metrics
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {Object.entries(executiveSummary.key_metrics).slice(0, 6).map(([key, value]) => (
                                        <div key={key} className="p-4 bg-sand-100 rounded-xl border border-[rgba(32,30,29,0.12)] hover:bg-sand-200 transition-colors">
                                            <div className="text-sm text-sand-600 capitalize mb-1">{key.replace(/_/g, ' ')}</div>
                                            <div className="text-xl font-bold text-ink tnum">{safeRender(value)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Recommended Analyses */}
                        {executiveSummary.recommended_analyses && (
                            <div>
                                <h4 className="text-base font-semibold text-ink mb-3 flex items-center gap-2">
                                    <Lightbulb className="w-5 h-5 text-ink" />
                                    Suggested Questions
                                </h4>
                                <div className="flex flex-wrap gap-3">
                                    {safeArray(executiveSummary.recommended_analyses).slice(0, 4).map((suggestion, idx) => (
                                        <motion.button
                                            key={idx}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => handleSuggestedQuestion(suggestion)}
                                            className="px-4 py-2 text-sm bg-white border border-[rgba(32,30,29,0.18)] rounded-lg hover:bg-sand-100 transition-colors text-ink font-medium"
                                        >
                                            {safeRender(suggestion)}
                                        </motion.button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-8 text-sand-600">
                        <p className="text-base">Click refresh to generate AI analysis of your dataset</p>
                    </div>
                )}
            </motion.div>

            {/* Question Input */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card"
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-sand-100 rounded-lg border border-[rgba(32,30,29,0.12)]">
                        <Sparkles className="w-5 h-5 text-ink" />
                    </div>
                    <h2 className="text-xl font-semibold text-ink">Ask a Business Question</h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="relative">
                        <textarea
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="Ask complex business questions... e.g., 'What are the top performing products and what drives their success?'"
                            className="input-field min-h-[100px] resize-none pr-12"
                            disabled={loading}
                        />
                        <motion.button
                            type="submit"
                            disabled={loading || !question.trim()}
                            className="absolute bottom-3 right-3 p-2 bg-bill-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bill-600 shadow-brand-sm"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <Send className="w-5 h-5" />
                            )}
                        </motion.button>
                    </div>
                </form>
            </motion.div>

            {/* Error Display */}
            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="card border-red-200 bg-red-50"
                    >
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-red-700">Analysis Error</h4>
                                <p className="text-sm text-red-600 mt-1">{error}</p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Analysis Results */}
            <AnimatePresence>
                {analysis && analysis.success && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="space-y-6"
                    >
                        {/* Key Insight */}
                        {analysis.insights?.key_insight && (
                            <div className="card bg-sage-100 border border-sage-200">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="p-2 bg-sage-200 rounded-lg border border-sage-300">
                                        <CheckCircle className="w-5 h-5 text-sage-700" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-sage-900">Key Insight</h3>
                                </div>
                                <p className="text-sage-900 text-lg leading-relaxed">{safeRender(analysis.insights.key_insight)}</p>
                            </div>
                        )}

                        {/* Detailed Analysis */}
                        {analysis.insights?.detailed_analysis && (
                            <div className="card">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="p-2 bg-sand-100 rounded-lg border border-[rgba(32,30,29,0.12)]">
                                        <FileText className="w-5 h-5 text-ink" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-ink">Detailed Analysis</h3>
                                </div>
                                <div className="prose prose-sm max-w-none text-sand-700">
                                    {safeRender(analysis.insights.detailed_analysis)}
                                </div>
                            </div>
                        )}

                        {/* Recommendations */}
                        {analysis.insights?.recommendations && safeArray(analysis.insights.recommendations).length > 0 && (
                            <div className="card">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="p-2 bg-sand-100 rounded-lg border border-[rgba(32,30,29,0.12)]">
                                        <Lightbulb className="w-5 h-5 text-ink" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-ink">Recommendations</h3>
                                </div>
                                <ul className="space-y-2">
                                    {safeArray(analysis.insights.recommendations).map((rec, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-sand-700">
                                            <ChevronRight className="w-4 h-4 text-sand-600 mt-1 flex-shrink-0" />
                                            <span>{safeRender(rec)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Follow-up Questions */}
                        {analysis.insights?.follow_up_questions && safeArray(analysis.insights.follow_up_questions).length > 0 && (
                            <div className="card">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="p-2 bg-sand-100 rounded-lg border border-[rgba(32,30,29,0.12)]">
                                        <List className="w-5 h-5 text-ink" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-ink">Explore Further</h3>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {safeArray(analysis.insights.follow_up_questions).map((q, idx) => (
                                        <motion.button
                                            key={idx}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => handleSuggestedQuestion(q)}
                                            className="px-4 py-2 text-sm bg-white border border-[rgba(32,30,29,0.18)] rounded-lg hover:bg-sand-100 transition-colors text-ink"
                                        >
                                            {safeRender(q)}
                                        </motion.button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* SQL Query */}
                        <div className="card">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-sand-100 rounded-lg border border-[rgba(32,30,29,0.12)]">
                                    <BarChart3 className="w-5 h-5 text-ink" />
                                </div>
                                <h3 className="text-lg font-semibold text-ink">Generated Query</h3>
                                <span className="ml-auto text-sm text-sand-600 tnum">
                                    {analysis.executionTime?.toFixed(1)}ms • {analysis.rowCount} rows
                                </span>
                            </div>
                            <pre className="bg-ink p-4 rounded-lg overflow-x-auto text-sm font-mono text-paper border border-[rgba(32,30,29,0.12)]">
                                {analysis.sql}
                            </pre>
                        </div>

                        {/* Results Table */}
                        {analysis.results && analysis.results.length > 0 && (
                            <div className="card">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-ink">Data Results</h3>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => exportService.exportToCSV(analysis.results, `query_results_${Date.now()}`)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white hover:bg-sand-100 border border-[rgba(32,30,29,0.18)] rounded-lg text-sand-700 hover:text-ink transition-colors"
                                        >
                                            <Download className="w-4 h-4" />
                                            CSV
                                        </button>
                                        <button
                                            onClick={() => exportService.exportToExcel(analysis.results, `query_results_${Date.now()}`)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white hover:bg-sand-100 border border-[rgba(32,30,29,0.18)] rounded-lg text-sand-700 hover:text-ink transition-colors"
                                        >
                                            <Download className="w-4 h-4" />
                                            Excel
                                        </button>
                                    </div>
                                </div>
                                <div className="table-container">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-sand-100">
                                                <tr>
                                                    {Object.keys(analysis.results[0]).map((col) => (
                                                        <th key={col} className="px-4 py-3 text-left text-sm font-medium text-sand-600">
                                                            {col}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-[rgba(32,30,29,0.08)]">
                                                {analysis.results.slice(0, 10).map((row, idx) => (
                                                    <tr key={idx} className="hover:bg-sand-100">
                                                        {Object.values(row).map((val, cidx) => (
                                                            <td key={cidx} className="px-4 py-3 text-sm text-sand-700 tnum">
                                                                {val === null ? <span className="text-sand-500 italic">null</span> : String(val)}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                {analysis.results.length > 10 && (
                                    <div className="px-4 py-3 bg-sand-100 text-sm text-sand-600 text-center border-t border-[rgba(32,30,29,0.08)]">
                                        Showing first 10 of {analysis.results.length} results
                                    </div>
                                )}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Analysis History */}
            {analysisHistory.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="card"
                >
                    <h3 className="text-lg font-semibold text-ink mb-4">Recent Analyses</h3>
                    <div className="space-y-3">
                        {analysisHistory.slice(0, 5).map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                className="p-3 bg-sand-100 rounded-lg cursor-pointer hover:bg-sand-200 transition-colors border border-[rgba(32,30,29,0.08)]"
                                onClick={() => setQuestion(item.question)}
                            >
                                <div className="flex items-center justify-between">
                                    <p className="text-sm text-sand-700 flex-1">{item.question}</p>
                                    <div className="flex items-center gap-2 text-xs text-sand-600 tnum">
                                        <span>{item.executionTime?.toFixed(1)}ms</span>
                                        <span>•</span>
                                        <span>{item.rowCount} rows</span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default BusinessAnalysis;
